import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    output,
    viewChildren,
} from '@angular/core';
import {
    applySortingToHeaders,
    LoadComponent,
    SortableTableHeaderComponent,
    Sorting,
} from '@brunata-angular/ui';

export type ExportTableColumn = {
    label: string;
    sortColumn?: string;
    alignEnd?: boolean;
    width?: string;
};

@Component({
    selector: 'app-export-table',
    standalone: true,
    imports: [LoadComponent, SortableTableHeaderComponent],
    template: `
        <div [class.table-responsive]="responsive()">
            <table
                class="table panel-table align-middle mb-0"
                [class.table-hover]="!loading()"
                [class.table-striped]="!loading()"
                [class.export-table-fixed]="hasColumnWidths()">
                <thead>
                    <tr>
                        @for (column of columns(); track column.label) {
                            @if (column.sortColumn; as sortColumn) {
                                <th
                                    scope="col"
                                    app-sortable-table-header
                                    [style.width]="column.width"
                                    [column]="sortColumn"
                                    [label]="column.label"
                                    (sort)="sort.emit($event)"></th>
                            } @else {
                                <th
                                    scope="col"
                                    [style.width]="column.width"
                                    [class.text-end]="column.alignEnd === true">
                                    {{ column.label }}
                                </th>
                            }
                        }
                    </tr>
                    <ng-content select="[appExportTableFilters]" />
                </thead>
                <tbody>
                    @if (loading()) {
                        <tr>
                            <td class="border-0" [attr.colspan]="columns().length">
                                <app-load fadeSpeed="FAST" />
                            </td>
                        </tr>
                    } @else {
                        <ng-content />
                    }
                </tbody>
            </table>
        </div>
    `,
    styles: `
        .export-table-fixed {
            table-layout: fixed;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportTableComponent {
    readonly columns = input.required<ExportTableColumn[]>();
    readonly currentSorting = input<Sorting | null>(null);
    readonly responsive = input(true);
    readonly loading = input(false);

    protected readonly hasColumnWidths = computed(() =>
        this.columns().some(column => column.width !== undefined),
    );
    readonly sort = output<Sorting>();

    private readonly sortHeaders = viewChildren(SortableTableHeaderComponent);

    constructor() {
        effect(() =>
            applySortingToHeaders(
                this.sortHeaders(),
                this.currentSorting() ?? {column: '', direction: null},
            ),
        );
    }
}
